/**
 * The Figma inbox row's cells: creator (with the unread dot), message
 * (subject over preview), status (one line of pills) and last activity (time over the next-step line).
 */
import type { OutreachRow } from '@discovr/types';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { CreatorLookupBadge } from '../CreatorsTab/CreatorLookupBadge';
import { resolveCreatorLookupBadge } from '../CreatorsTab/creatorLookupStatus';
import { OutreachRowLine, OutreachRowPills } from './OutreachRowPills';
import { FOCUS_RING, StatusTooltip } from './StatusTooltip';
import { inboxMessage, inboxTime } from './inboxRowText';

function initials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export interface InboxCreatorCellProps {
  row: OutreachRow;
  /** The name is its own click target (Figma: matches the Creators tab's
   *  identity-opens-profile pattern) — opens the profile, independent of
   *  whatever the rest of the row's own click does (the thread). Optional
   *  only so standalone renders (e.g. this file's own stories/tests) don't
   *  need it; the row always passes it in practice. */
  onViewProfile?: (entryId: string) => void;
}

export function InboxCreatorCell({ row, onViewProfile }: InboxCreatorCellProps) {
  const name = row.displayName || row.username;
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar size="lg">
        {row.avatarUrl && <AvatarImage src={row.avatarUrl} alt={name} />}
        <AvatarFallback>{initials(name)}</AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 items-center gap-1.5">
          {row.unread && (
            <StatusTooltip content="Unread message">
              <span role="img" aria-label="Unread" tabIndex={0} className={`size-1.5 shrink-0 rounded-full bg-(--brand-500) ${FOCUS_RING}`} />
            </StatusTooltip>
          )}
          <button
            type="button"
            aria-label={`View profile for ${name}`}
            title={name}
            onClick={(e) => {
              e.stopPropagation();
              onViewProfile?.(row.entryId);
            }}
            className="truncate text-left text-base font-medium text-foreground hover:underline"
          >
            {name}
          </button>
        </div>
        {/* The lookup badge sits beside the handle, not under it, to keep Figma's 68px row. */}
        <div className="flex min-w-0 items-center gap-1.5 [&>[data-tone]]:shrink-0">
          <span title={`@${row.username}`} className="min-w-0 truncate text-sm text-muted-foreground">@{row.username}</span>
          <CreatorLookupBadge pill={resolveCreatorLookupBadge(row.lookupStatus, { isAdmin: true }).pill} />
        </div>
      </div>
    </div>
  );
}

export function InboxMessageCell({ row }: { row: OutreachRow }) {
  const message = inboxMessage(row);
  if (!message) return null;
  return (
    <div className="flex min-w-0 flex-col">
      {message.subject && (
        <span className="truncate text-sm font-medium text-foreground" title={message.subject}>
          {message.subject}
        </span>
      )}
      <span className="truncate text-sm text-muted-foreground" title={message.preview}>
        {message.preview}
      </span>
    </div>
  );
}

export function InboxStatusCell({
  row,
  campaignZone,
  hideContactedPill,
  autoFollowups,
}: {
  row: OutreachRow;
  campaignZone?: string;
  hideContactedPill?: boolean;
  autoFollowups?: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-2">
      <OutreachRowPills row={row} campaignZone={campaignZone} hideContactedPill={hideContactedPill} autoFollowups={autoFollowups} />
    </div>
  );
}

/** The last-activity time over the row's next-step line (Figma's "N opens" slot). */
export function InboxLastActivityCell({ row, campaignZone, autoFollowups }: { row: OutreachRow; campaignZone?: string; autoFollowups?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col items-end">
      <span className="text-sm font-medium text-(--foreground-secondary-default)">{inboxTime(row.lastActivityAt)}</span>
      <OutreachRowLine row={row} campaignZone={campaignZone} autoFollowups={autoFollowups} className="max-w-full text-right" />
    </div>
  );
}
